import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-styled-flexboxgrid';
import styled from 'styled-components';
import { StoreContext } from '../../context/store-context';
import { bpWidth, colors } from '../../styles/variables';
import CartBody from './CartBody';
import CartFooter from './CartFooter';
import CartHeader from './CartHeader';

const CartRowOuter = styled(Row)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 22;
  width: 100vw;
  height: 100dvh;

  display: flex;
  visibility: hidden;
  opacity: 0;
  transition:
    visibility 0s,
    opacity 0s linear;
  transition-delay: 250ms;

  ${(props) =>
    props.cartIsOpen === true
      ? `
      visibility: visible;
      opacity: 1;
      transition-delay: 0s;
    `
      : ''};
`;
const CartColOuter = styled(Col)`
  background: none;
  height: ${(props) => props.height}px;
  transition: background-color 100ms ease;

  ${(props) =>
    props.cartIsOpen === true
      ? `
      background-color: rgba(243, 241, 237, .7);
    `
      : ''};
`;
const CartRowInner = styled(Row)`
  height: 100dvh;
`;
const CartColInner = styled(Col)`
  background-color: ${colors.beige100};
  opacity: 1;
  height: 100%;
  width: 528px;
  max-width: 528px;
  flex-basis: 528px;

  transform: ${(props) => (props.cartIsOpen === true ? 'translateX(0%)' : 'translateX(100%)')};
  transition: transform 250ms ease;

  @media (${bpWidth.desktopSm}) {
    width: 444px;
    max-width: 444px;
    flex-basis: 444px;
  }

  @media (${bpWidth.tablet}) {
    width: 100%;
    max-width: 100%;
    flex-basis: 100%;
  }
`;

// should refactor these component names, but I needed them in order to sticky the CartFooter to the bottom of the Cart
const CarRowInnerInner = styled(Row)`
  height: 100%;
`;
const CartColInnerInner = styled(Col)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Cart = React.forwardRef((props, ref) => {
  const { cart, isOpen: cartIsOpen } = useContext(StoreContext);

  const cartRowOuterEl = useRef(null);
  const cartColOuterEl = useRef(null);
  const cartColInnerEl = ref;
  const accessoriesContainerEl = useRef(null);
  const accessoriesHeaderRowEl = useRef(null);
  const accessoriesBodyRowEl = useRef(null);

  const [accessoriesExpanded, setAccessoriesExpanded] = useState(false);
  const [lastLineItemBottomPadding, setLastLineItemBottomPadding] = useState(140);

  // an array containing only the main product titles currently in cart as strings
  // so if a gitaplus is in cart, this array will contain a string "gitaPLUS"
  // if a gitamini is in cart, this array will contain a string "gita mini" (this is how the handle is written in Shopify)
  const mainProductsInCart = useMemo(() => {
    if (cart.totalQuantity === 0) {
      return [];
    }

    return cart.lines?.edges
      ?.filter(
        (edge) =>
          edge?.node?.merchandise?.product?.title === 'gitaplus' ||
          edge?.node?.merchandise?.product?.title === 'gitamini' ||
          edge?.node?.merchandise?.product?.title === 'G1T4-M1N1',
      )
      .map((edge) => edge?.node?.merchandise?.product?.title)
      .reduce((a, b) => {
        if (a.indexOf(b) < 0) a.push(b);
        return a;
      }, []);
  }, [cart]);

  const handleAccessoriesClick = () => {
    setAccessoriesExpanded(!accessoriesExpanded);
  };

  // set padding for last line item in cart
  // this is to account for the accessories container, which overlays the cart body
  // when the accessories container displays, add extra padding to allow the last line item to be visible
  useEffect(() => {
    if (accessoriesContainerEl.current && accessoriesHeaderRowEl.current) {
      const accessoriesContainerElOffsetHeight = accessoriesContainerEl.current.offsetHeight;
      const accessoriesHeaderRowElOffsetHeight = accessoriesHeaderRowEl.current.offsetHeight;

      if (!accessoriesExpanded) {
        setLastLineItemBottomPadding(accessoriesHeaderRowElOffsetHeight + 40);
      } else {
        setLastLineItemBottomPadding(accessoriesContainerElOffsetHeight + 40);
      }
    }
  }, [accessoriesContainerEl, accessoriesHeaderRowEl, accessoriesExpanded]);

  // reset accessoriesExpanded when cart is collapsed
  useEffect(() => {
    if (!cartIsOpen) {
      setAccessoriesExpanded(false);
    }
  }, [cartIsOpen]);

  // close accessories container when clicking outside of it, but still within the cart
  function useHandleOutsideAccessoriesContainerClick(_ref) {
    useEffect(() => {
      function handleOutsideAccessoriesContainerClick(event) {
        if (_ref.current && accessoriesExpanded && !_ref.current.contains(event.target)) {
          // pointer-events CSS property is set via props on the CartBody,
          // preventing cart body interaction until accessories container is collapsed
          handleAccessoriesClick();
        }
      }

      document.addEventListener('click', handleOutsideAccessoriesContainerClick);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('click', handleOutsideAccessoriesContainerClick);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, accessoriesExpanded]);
  }

  useHandleOutsideAccessoriesContainerClick(accessoriesContainerEl);

  // lock body scroll while cart is open
  useEffect(() => {
    // helper function
    const preventDefault = (e) => {
      e.preventDefault();
    };

    if (cartIsOpen) {
      if (cartColOuterEl && cartRowOuterEl.current) {
        cartRowOuterEl.current.addEventListener('pointermove', preventDefault);
      }
    }
    return () => {
      if (cartColOuterEl && cartRowOuterEl.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        cartRowOuterEl.current.removeEventListener('pointermove', preventDefault);
      }
    };
  }, [cartIsOpen]);

  return (
    <CartRowOuter cartIsOpen={cartIsOpen} ref={cartRowOuterEl}>
      <CartColOuter xs={8} sm={8} md={10} lg={12} cartIsOpen={cartIsOpen} ref={cartColOuterEl}>
        <CartRowInner end="md" id="outerCartEl">
          <CartColInner xs={8} sm={8} md={4} lg={4} cartIsOpen={cartIsOpen} ref={cartColInnerEl}>
            <CarRowInnerInner>
              <CartColInnerInner xs={8} sm={8} md={10} lg={12}>
                <CartHeader />
                <CartBody
                  accessoriesExpanded={accessoriesExpanded}
                  ref={accessoriesContainerEl}
                  lastLineItemBottomPadding={lastLineItemBottomPadding}
                />
                <CartFooter
                  ref={{ accessoriesContainerEl, accessoriesHeaderRowEl, accessoriesBodyRowEl }}
                  showAccessories={mainProductsInCart.length > 0}
                  accessoriesExpanded={accessoriesExpanded}
                  handleAccessoriesClick={handleAccessoriesClick}
                  mainProductsInCart={mainProductsInCart}
                />
              </CartColInnerInner>
            </CarRowInnerInner>
          </CartColInner>
        </CartRowInner>
      </CartColOuter>
    </CartRowOuter>
  );
});

export default Cart;
